import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { productAPI } from "@/services/api";
import { BACKEND_ORIGIN } from "@/config/api";
import { Code2, Copy, ExternalLink, Key, Package, Send } from "lucide-react";
import {
  TailAdminCodeBlock,
  TailAdminEmptyState,
  TailAdminFormSection,
  TailAdminPageHeader,
  TailAdminPanel,
  TailAdminStatusBadge,
} from "@/components/template/tailadmin/DashboardPrimitives";

const getProductId = (product) => product?.productId || product?.id || product?._id || "";
const getApiKey = (product) => product?.apiKey || product?.api_key || "";

const ClientSDKSetup = () => {
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      try {
        const response = await productAPI.getProducts();
        const nextProducts = Array.isArray(response.data) ? response.data : response.data?.products || [];
        setProducts(nextProducts);
        if (nextProducts.length > 0) setSelectedProductId(getProductId(nextProducts[0]));
      } catch {
        toast.error("Failed to load products");
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, []);

  const selectedProduct = products.find((product) => getProductId(product) === selectedProductId) || null;
  const backendUrl = BACKEND_ORIGIN;
  const apiKey = getApiKey(selectedProduct);

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  if (!loading && products.length === 0) {
    return (
      <TailAdminEmptyState
        icon={Package}
        title="No products found"
        description="Create a Prove-Request first so Proovam can generate a campaign integration API key."
      />
    );
  }

  return (
    <div className="min-w-0 space-y-6">
      <TailAdminPageHeader
        eyebrow="Developer setup"
        title="Campaign Integration"
        description="Connect tracker.js and webhooks to verify conversions for a Prove-Request."
        actions={
          <Select value={selectedProductId} onValueChange={setSelectedProductId} disabled={loading || products.length === 0}>
            <SelectTrigger className="h-11 w-full sm:w-80">
              <SelectValue placeholder="Select product" />
            </SelectTrigger>
            <SelectContent>
              {products.map((product) => (
                <SelectItem key={getProductId(product)} value={getProductId(product)}>
                  {product.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      <div className="grid min-w-0 gap-6 xl:grid-cols-[0.85fr_1.15fr]">
        <div className="min-w-0 space-y-6">
          <TailAdminFormSection
            icon={Key}
            title="Product API Key"
            description="Send this in the X-API-Key header when reporting conversions."
          >
            <div className="flex flex-col gap-2 sm:flex-row">
              <code className="min-w-0 flex-1 break-all rounded-xl border border-gray-100 bg-gray-50 p-3 font-mono text-sm text-gray-700">
                {apiKey || "No key available"}
              </code>
              <Button variant="outline" onClick={() => copyToClipboard(apiKey, "API key")} disabled={!apiKey}>
                <Copy className="h-4 w-4" />
                Copy
              </Button>
            </div>
            <p className="mt-3 text-xs text-gray-500">Keep this key out of version control and client-side public code.</p>
          </TailAdminFormSection>

          <TailAdminPanel title="Webhook endpoint" description="Call this endpoint after a user converts.">
            <div className="space-y-3">
              <TailAdminStatusBadge tone="primary">POST</TailAdminStatusBadge>
              <div className="flex flex-col gap-2 sm:flex-row">
                <code className="flex-1 break-all rounded-xl border border-gray-100 bg-gray-50 p-3 font-mono text-sm text-gray-700">
                  {backendUrl}/api/sdk/convert
                </code>
                <Button variant="outline" onClick={() => copyToClipboard(`${backendUrl}/api/sdk/convert`, "Endpoint")}>
                  <Copy className="h-4 w-4" />
                  Copy
                </Button>
              </div>
            </div>
          </TailAdminPanel>

          <Button
            variant="outline"
            className="w-full justify-center"
            onClick={() => window.open(`${backendUrl}/docs`, "_blank")}
          >
            <ExternalLink className="h-4 w-4" />
            Open API Docs
          </Button>
        </div>

        <div className="min-w-0 space-y-6">
          <TailAdminFormSection icon={Send} title="Request body" description="Minimum payload for a conversion event.">
            <TailAdminCodeBlock>
{`{
  "goal_type": "subscription",
  "link_token": "pv_abc123",
  "click_id": "opaque-click-receipt",
  "enrollment_id": "enr_xyz",
  "metadata": {
    "plan": "premium",
    "amount": 99.99
  }
}`}
            </TailAdminCodeBlock>
          </TailAdminFormSection>

          <TailAdminFormSection icon={Code2} title="Node.js example" description="Founder-owned same-origin route that signs the exact request body on your server.">
            <TailAdminCodeBlock>
{`import crypto from "node:crypto";

app.post("/api/proovam/conversion", async (req, res) => {
  const enrollment = await loadVerifiedEnrollmentForSession(req.session);
  if (!enrollment) return res.status(409).json({ error: "No verified enrollment" });
  const { link_token, click_id, consent_id } = req.body;
  if (typeof link_token !== "string" || typeof click_id !== "string") {
    return res.status(400).json({ error: "Missing attribution receipt" });
  }
  // Allowlist browser receipts; derive business values from server state.
  const payload = {
    goal_type: "subscription",
    link_token,
    click_id,
    consent_id,
    enrollment_id: enrollment.id,
    user_id: enrollment.customerReference,
    metadata: {
      revenue_minor: enrollment.subscriptionAmountMinor,
      currency: "NGN"
    }
  };
  const body = JSON.stringify(payload);
  const timestamp = Math.floor(Date.now() / 1000).toString();
  const signature = crypto
    .createHmac("sha256", process.env.PROOVAM_SIGNING_SECRET)
    .update(timestamp + "." + body)
    .digest("hex");

  const response = await fetch("${backendUrl}/api/sdk/convert", {
    method: "POST",
    headers: {
      "X-API-Key": process.env.PROOVAM_API_KEY,
      "X-Proovam-Sig": signature,
      "X-Proovam-Ts": timestamp,
      "Content-Type": "application/json"
    },
    body
  });
  res.status(response.status).send(await response.text());
});

// Keep PROOVAM_API_KEY and PROOVAM_SIGNING_SECRET server-only.`}
            </TailAdminCodeBlock>
          </TailAdminFormSection>
        </div>
      </div>
    </div>
  );
};

export default ClientSDKSetup;
